import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import {
  IconDownload,
  IconLoader2,
  IconRefresh,
  IconRotate,
  IconX,
  IconZoomIn,
  IconZoomOut,
} from '@tabler/icons-react';
import { downloadFileByUrl } from '../../lib/utils';

interface Props {
  src: string;
  getDownloadUrl: () => Promise<string>;
  filename: string;
  onClose: () => void;
}

const MIN_SCALE = 1;
const MAX_SCALE = 8;
const ZOOM_STEP = 1.25;

const ImageLightbox: React.FC<Props> = ({ src, getDownloadUrl, filename, onClose }) => {
  const [loaded, setLoaded] = useState(false);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);

  const overlayRef = useRef<HTMLDivElement>(null);
  const scaleRef = useRef(scale);

  scaleRef.current = scale;

  useEffect(() => {
    if (scale <= MIN_SCALE) setOffset({ x: 0, y: 0 });
  }, [scale]);

  // Escape
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [onClose]);

  // Wheel zoom toward cursor
  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
      const newScale = Math.min(Math.max(scaleRef.current * factor, MIN_SCALE), MAX_SCALE);
      const ratio = newScale / scaleRef.current;
      const dx = e.clientX - window.innerWidth / 2;
      const dy = e.clientY - window.innerHeight / 2;
      setScale(newScale);
      setOffset(prev => ({
        x: dx + (prev.x - dx) * ratio,
        y: dy + (prev.y - dy) * ratio,
      }));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  };

  const zoomIn = useCallback(() => setScale(s => Math.min(s * ZOOM_STEP, MAX_SCALE)), []);
  const zoomOut = useCallback(() => setScale(s => Math.max(s / ZOOM_STEP, MIN_SCALE)), []);
  const rotateLeft = useCallback(() => setRotation(r => r - 90), []);
  const rotateRight = useCallback(() => setRotation(r => r + 90), []);
  const reset = useCallback(() => { setScale(1); setOffset({ x: 0, y: 0 }); setRotation(0); }, []);

  const imgTransform = useMemo(
    () => `translate(${offset.x}px, ${offset.y}px) scale(${scale}) rotate(${rotation}deg)`,
    [offset.x, offset.y, scale, rotation],
  );

  const handleDownload = useCallback(() => {
    void getDownloadUrl().then(url => downloadFileByUrl(url, filename));
  }, [getDownloadUrl, filename]);

  const stopPropagation = (e: React.MouseEvent) => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); };

  return (
    <div
      ref={overlayRef}
      className="lightbox-overlay"
      onClick={handleOverlayClick}
    >
      <button
        className="lightbox-close"
        type="button"
        onClick={onClose}
        onMouseDown={stopPropagation}
        aria-label="Закрыть"
      >
        <IconX size={20} stroke={2} />
      </button>

      <div className="lightbox-content">
        {!loaded && (
          <div className="lightbox-spinner">
            <IconLoader2 size={36} stroke={1.5} className="spin" />
          </div>
        )}
        <img
          className={`lightbox-img${loaded ? ' is-loaded' : ''}`}
          src={src}
          alt={filename}
          draggable={false}
          style={{ transform: imgTransform }}
          onLoad={() => flushSync(() => setLoaded(true))}
        />
      </div>

      <div className="lightbox-toolbar" onClick={stopPropagation} onMouseDown={stopPropagation}>
        <button className="lb-btn" type="button" onClick={zoomOut} title="Уменьшить" disabled={scale <= MIN_SCALE}>
          <IconZoomOut size={18} stroke={2} />
        </button>
        <span className="lb-scale">{Math.round(scale * 100)}%</span>
        <button className="lb-btn" type="button" onClick={zoomIn} title="Увеличить" disabled={scale >= MAX_SCALE}>
          <IconZoomIn size={18} stroke={2} />
        </button>
        <div className="lb-sep" />
        <button className="lb-btn" type="button" onClick={rotateLeft} title="Повернуть влево">
          <IconRotate size={18} stroke={2} style={{ transform: 'scaleX(-1)' }} />
        </button>
        <button className="lb-btn" type="button" onClick={rotateRight} title="Повернуть вправо">
          <IconRotate size={18} stroke={2} />
        </button>
        <div className="lb-sep" />
        <button className="lb-btn" type="button" onClick={reset} title="Сбросить вид">
          <IconRefresh size={18} stroke={2} />
        </button>
        <button className="lb-btn" type="button" onClick={handleDownload} title="Скачать">
          <IconDownload size={18} stroke={2} />
        </button>
      </div>
    </div>
  );
};

export { ImageLightbox };
